import { forwardRef, InputHTMLAttributes, LegacyRef, useMemo, useRef, useState } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { DayPicker } from 'react-day-picker';
import dayjs from 'dayjs';
import { useOnClickOutside } from 'usehooks-ts';

type DatePickerInputProps = { onChange?: (value: string | undefined) => void } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color' | 'onChange' | 'value'
> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    disabledDates?: ((date: Date) => boolean) | Date[];
  } & { value: string; displayFormat?: string; timeZone?: string };

const DatePickerInput = forwardRef(
  (
    {
      label,
      error,
      wrapperClassName,
      size = 'md',
      color,
      bordered = true,
      value,
      onChange,
      className,
      displayFormat = 'MM/DD/YYYY',
      timeZone,
      disabledDates,
      ...args
    }: DatePickerInputProps,
    ref: LegacyRef<HTMLInputElement>
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const calendarValue = useMemo(() => {
      return value ? dayjs(value).toDate() : undefined;
    }, [value]);

    const handleSelect = (value: Date | undefined) => {
      if (onChange) {
        // Format date as YYYY-MM-DD to avoid timezone issues
        onChange(value ? dayjs(value).format('YYYY-MM-DD') : undefined);
      }
      // @ts-expect-error - blur() is a valid method
      document?.activeElement?.blur();
      setIsOpen(false);
    };

    const handleClickOutside = () => {
      setIsOpen(false);
    };

    useOnClickOutside(wrapperRef, handleClickOutside);

    return (
      <div ref={wrapperRef} className={cn('form-control max-w-full w-full group', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <label
          className={cn('input flex items-center justify-between gap-2 dropdown cursor-pointer max-w-full', {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md' || size,
            'input-lg': size === 'lg',
            'input-bordered': bordered,
          })}
        >
          <input
            tabIndex={0}
            className={cn('text-base grow cursor-pointer overflow-auto max-w-[calc(100%-1rem)]', className)}
            ref={ref}
            readOnly
            value={value ? dayjs(value).format(displayFormat) : ''}
            onFocus={() => setIsOpen(true)}
            {...args}
          />
          <div
            tabIndex={0}
            className={cn(
              'dropdown-content bg-base-100 rounded-box z-10 p-2 sm:p-4 shadow-sm absolute left-0 sm:left-[50%] sm:-translate-x-[50%] top-[calc(100%+5px)] hidden group-focus-within:flex max-w-[calc(100vw-2rem)] overflow-x-auto',
              {
                'opacity-100 visible flex scale-100': isOpen,
              }
            )}
          >
            <DayPicker
              timeZone={timeZone}
              captionLayout="dropdown"
              mode="single"
              selected={calendarValue}
              onSelect={handleSelect}
              disabled={disabledDates}
            />
          </div>
          <CalendarIcon className="w-4 shrink-0" />
        </label>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
);

export default DatePickerInput;
